import { cn } from "@/lib/utils"

/**
 * A quiet text action — the way every action in the rebuild reads.
 *
 * The canvas is explicit that this is the product's action language: teal is
 * "used only where it means something", so a control is a word, in teal when it
 * is the one you came for and in muted grey when it is not.
 *
 * One exception, added by canvas turn 33: a page's single primary create is a
 * filled pill rather than a word — `PageAction` with `tone="primary"`. That is
 * the only filled control on a page, and everything else, here included, stays
 * a word. Bordered boxes are still pre-Graphite.
 *
 * This lived inside the comments screen and the class string had been copied
 * into a dozen files besides. It is one component now; reach for it rather than
 * writing the fourteenth copy.
 */
export function TextAction({
  children,
  tone = "quiet",
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: "teal" | "quiet" }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-[7px] text-[12px] transition-opacity hover:opacity-80",
        tone === "teal"
          ? "font-medium text-brand-ink"
          : "text-muted-foreground hover:text-foreground",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}
